import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { retry } from 'rxjs/operators';
import { ZipService } from "src/app/services/zip.service";
import { environment } from 'src/environments/environment';
import { ReportInfo } from '../data/book';
import { ConfigService } from './config.service';
import { ImagesCacheService } from './images-cache.service';

@Injectable({
  providedIn: 'root'
})
export class ReportService {

  constructor(
    private configService: ConfigService,
    private http: HttpClient,
    private domSanitizer: DomSanitizer,
    private imagesService: ImagesCacheService,
    private zipService: ZipService
  ) { }

  // htmlReport: SafeHtml | undefined = undefined;
  reports: { data: SafeHtml | string, auxData: SafeHtml[] | string[], type: 'pdf' | 'png' | 'html', studyUID: string }[] = [];
  // actualReport es solo para optimizar la carga
  actualReport: { data: SafeHtml | string, auxData: SafeHtml[] | string[], type: 'pdf' | 'png' | 'html', studyUID: string } | undefined;
  reportIndex: number = 0;

  activatedAI: boolean = false;
  aiData: {
    patient_history: string,
    terminology: string,
    causes: string,
    treatment: string,
    prevention: string,
    complementary_tests: string
  } | null = null;

  async loadReports(reports: ReportInfo[] | undefined) {
    if (reports == undefined || reports.length == 0)
      return;

    for (let i = 0; i < reports.length; i++) {
      const r = reports[i];
      if (r?.pngs != undefined) {
        for (let j = 0; j < r.pngs.length; j++) {
          const png = r.pngs[j]
          if (png != undefined) {
            let rep: any = undefined;
            if (png.toLowerCase().endsWith(".html")) {
              rep = await this.loadHtmlReport(r.studyUID, png);
            } else if (png.toLowerCase().endsWith(".pdf")) {
              rep = await this.loadPDFReport(r.studyUID, png);
            } else if (png.toLowerCase().endsWith(".png") || png.toLowerCase().endsWith(".jpg")) {
              rep = await this.loadPNGReport(r.studyUID, png);
            }
            if (rep != undefined)
              this.reports.push(rep);
          }
        }
      }
    }
    if (this.reports.length > 0)
      this.actualReport = this.reports[0];
  }

  public hasNext() {
    return this.reportIndex < (this.reports.length - 1) && !this.activatedAI;
  }

  public hasPrev() {
    return this.reportIndex > 0 && !this.activatedAI;
  }

  public nextReport() {
    if (this.hasNext()) {
      this.reportIndex++;
      this.actualReport = this.reports[this.reportIndex];
    }
  }

  public prevReport() {
    if (this.hasPrev()) {
      this.reportIndex--;
      this.actualReport = this.reports[this.reportIndex];
    }
  }

  private loadHtmlReport(studyUID: string, route: string): Promise<typeof this.actualReport> {
    return new Promise<typeof this.actualReport>((resolve, reject) => {
      if (!environment.demo) {

        this.configService.BOOK_URL.then(bookUrl => {
          let url = bookUrl;
          url += '/bookAccess/getHtmlReport?studyUID=' + studyUID;
          this.http.post<string>(url, route, { withCredentials: true, responseType: 'string' as 'json' }).pipe(retry(3))
            .subscribe(
              next => {
                if (next == null) {
                  console.warn("No se ha encontrado el informe " + route)
                }

                resolve({
                  data: this.domSanitizer.bypassSecurityTrustHtml(next),
                  auxData: [this.domSanitizer.bypassSecurityTrustHtml(next)],
                  type: 'html',
                  studyUID: studyUID
                })
              },
              error => {
                console.error("No se ha encontrado el informe " + route, error)
                reject(error);
              });
        }).catch(err => {
          console.error(err);
          reject(err);
        })
      } else {
        this.http.get(route, { responseType: 'string' as 'json' }).subscribe(
          next => {
            resolve({
              data: this.domSanitizer.bypassSecurityTrustHtml(next as string),
              auxData: [this.domSanitizer.bypassSecurityTrustHtml(next as string)],
              type: 'html',
              studyUID: studyUID
            })
          },
          error => {
            reject(error);
          });
      }
    })
  }

  private loadPDFReport(studyUID: string, route: string): Promise<typeof this.actualReport> {
    return new Promise<typeof this.actualReport>((resolve, reject) => {
      if (!environment.demo) {
        this.configService.BOOK_URL.then(bookUrl => {
          let url = bookUrl;
          url += '/bookAccess/getPDFReport?studyUID=' + studyUID;
          this.http.post(url, route, { withCredentials: true, responseType: 'arraybuffer' }).pipe(retry(3))
            .subscribe(
              next => {
                if (next == null || next.byteLength == 0) {
                  console.error('Este estudio no dispone de informe. Póngase en contacto con el centro para más información')
                  reject('report devolvió nulo')
                } else {
                  const blob = new Blob([next], { type: 'application/pdf' });
                  const blobUrl = URL.createObjectURL(blob);

                  this.reportPDF2PNG(studyUID, route).then(pngReports => {
                    resolve({
                      data: blobUrl,
                      auxData: pngReports,
                      type: 'pdf',
                      studyUID: studyUID
                    })
                  }).catch(err => {
                    console.error(err);
                    resolve({
                      data: blobUrl,
                      auxData: [blobUrl],
                      type: 'pdf',
                      studyUID: studyUID
                    })
                  })
                }
              },
              error => {
                console.error("No se ha encontrado el informe " + route, error)
                reject(error);
              });
        }).catch(err => {
          console.error(err);
          reject(err);
        })
      } else {
        this.http.get(route, { responseType: 'arraybuffer' }).subscribe(
          next => {
            const blob = new Blob([next], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            resolve({
              data: blobUrl,
              auxData: [blobUrl],
              type: 'pdf',
              studyUID: studyUID
            })
          },
          error => {
            reject(error);
          });
      }
    })
  }

  private loadPNGReport(studyUID: string, route: string): Promise<typeof this.actualReport> {
    return new Promise<typeof this.actualReport>((resolve, reject) => {
      this.imagesService.getFile(studyUID, route).then(res => {
        if (res == null || res.size == 0) {
          console.error('Este estudio no dispone de informe. Póngase en contacto con el centro para más información')
          reject('report devolvió nulo')
        } else {
          const blobUrl = URL.createObjectURL(res);

          resolve({
            data: blobUrl,
            auxData: [blobUrl],
            type: 'png',
            studyUID: studyUID
          })
        }
      }).catch(err => {
        console.error(err);
        reject(err);
      })
    })
  }

  private reportPDF2PNG(studyUID: string, route: string): Promise<string[] | SafeHtml[]> {
    return new Promise<string[] | SafeHtml[]>((resolve, reject) => {
      if (!environment.demo) {
        this.configService.BOOK_URL.then(bookUrl => {
          let url = bookUrl;
          url += '/bookAccess/reportPDF2PNG?studyUID=' + studyUID;
          this.http.post(url, route, { withCredentials: true, responseType: 'arraybuffer' }).pipe(retry(3))
            .subscribe(
              {
                next: (res) => {
                  if (res == null) {
                    console.error('Este estudio no dispone de informe. Póngase en contacto con el centro para más información')
                    reject('report devolvió nulo')
                  } else {
                    const blob = new Blob([res], { type: 'application/zip' });

                    let finished = false;
                    const pngs: any[] = [];
                    this.zipService.getEntries(blob).subscribe(entries => {
                      entries.forEach((entry, index, arr) => {
                        const task = this.zipService.getData(entry);
                        task.data.subscribe(png => {
                          if (png.size > 0) {
                            const pngBlob = new Blob([png], { type: 'image/png' });
                            pngs.push(URL.createObjectURL(pngBlob));
                          }
                          if (index == arr.length - 1) {
                            finished = true
                          }
                        });
                      });
                    });

                    const interval = setInterval(() => {
                      if (finished) {
                        console.log('pngs', pngs);
                        resolve(pngs)
                        clearInterval(interval);
                      }
                    }, 100)
                  }
                },
                error: (err) => {
                  console.error("No se ha encontrado el informe " + route, err)
                  reject(err);
                }
              }
            );
        }).catch(err => {
          console.error(err);
          reject(err);
        })
      } else {
        this.http.get(route, { responseType: 'arraybuffer' }).subscribe(
          next => {
            const blob = new Blob([next], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            resolve([blobUrl])
          },
          error => {
            reject(error);
          });
      }
    })
  }

  loadAIData() {
    this.aiData = {
      patient_history: "<p>Resumen de la Historia Clínica del Paciente: Nerea Blanco López</p>" +
        "<p>Fecha de nacimiento: 11 de diciembre de 1972</p>" +
        "<ul>" +
        "<li>21 de mayo de 2021 - Ecografía Hombro Izquierdo" +
        "<ul>" +
        "<li>Diagnóstico: Leve bursitis subacromial-subdeltoidea. Hipertrofia degenerativa de la articulación acromioclavicular." +
        "<li>Tratamiento recomendado: Manejo conservador con fisioterapia y antiinflamatorios para el hombro." +
        "</ul>" +
        "<li>24 de mayo de 2022 - TAC Abdominal" +
        "<ul>" +
        "<li>Hallazgo principal: Litiasis cálcica de 6 mm en el riñón izquierdo (no obstructiva)." +
        "<li>Órganos normales: Hígado, páncreas, glándulas adrenales, bazo, y sistema urinario sin otras litiasis." +
        "<li>Conclusión: La piedra en el riñón izquierdo no es obstructiva y no requiere intervención inmediata." +
        "</ul>" +
        "<li>23 de noviembre de 2023 - TAC Abdominopélvico" +
        "<ul>" +
        "<li>Motivo: Persistencia de molestias tras litotricia." +
        "<li>Resultados: No se detectan litiasis en los riñones o uréteres. Se encuentran pequeñas piedras en la vejiga (litiasis vesicales puntiformes)." +
        "<li>Resto de los órganos (hígado, páncreas, glándulas suprarrenales, etc.) sin hallazgos significativos." +
        "<li>Conclusión: Presencia de pequeñas piedras en la vejiga; el resto del abdomen sin anomalías relevantes." +
        "</ul>" +
        "<li>29 de diciembre de 2023 - Ecografía Abdominal" +
        "<ul>" +
        "<li>Motivo: Chequeo general." +
        "<li>Resultados: Sin litiasis en los riñones ni en la vejiga (resolución de las piedras vesicales previamente observadas). Todos los órganos abdominales (hígado, vesícula, páncreas, bazo) en estado normal. Hiperplasia prostática leve (II/IV): Volumen prostático estimado en 40 cc." +
        "<li>Conclusión: Prostata levemente agrandada y sin otras anomalías en el abdomen." +
        "</ul>" +
        "</ul>" +
        "Resumen General:" +
        "<ul>" +
        "<li>Litiasis renal y vesical: La piedra en el riñón izquierdo detectada en 2022 fue tratada mediante litotricia. En el TAC de noviembre de 2023, las piedras renales desaparecieron, pero se encontraron pequeñas piedras en la vejiga, que luego ya no estaban presentes en el chequeo de diciembre." +
        "<li>Condiciones actuales: En diciembre de 2023, todos los órganos abdominales están normales, pero se ha detectado una leve hiperplasia prostática (agrandamiento de la próstata), sin síntomas relevantes por ahora." +
        "<li>Próximos pasos: Monitorear la hiperplasia prostática y la posible aparición de síntomas urinarios." +
        "</ul>" +
        "Consideraciones para la próxima visita:" +
        "<ul>" +
        "<li>Preguntar por posibles síntomas urinarios relacionados con la hiperplasia prostática (dificultad para orinar, flujo débil, frecuencia)." +
        "<li>Evaluar la necesidad de seguimiento periódico por la historia de litiasis renal y vesical." +
        "<li>Mantener una revisión general para prevenir nuevas formaciones de piedras y controlar el estado de la próstata." +
        "</ul>",
      terminology: "<ul>" +
        "<li><b>Plano sagital, axial y coronal:</b> Son diferentes planos o cortes del cuerpo en los que se toman las imágenes, proporcionando una visión tridimensional de la columna." +
        "<li><b>Lordosis lumbar fisiológica conservada:</b> La lordosis es la curvatura natural de la columna lumbar. 'Conservada' significa que esa curvatura es normal y no está alterada." +
        "<li><b>Pérdida de señal de los discos intersomáticos a nivel de L4-L5, L5-S1:</b> Los discos intervertebrales (que actúan como amortiguadores entre las vértebras) en los niveles L4-L5 y L5-S1 están mostrando degeneración. Esta pérdida de señal es un signo de desgaste o envejecimiento del disco." +
        "<li><b>Cambios modic tipo II:</b> Esto se refiere a alteraciones en las vértebras que rodean el disco L5-S1. Los cambios de tipo II están asociados con una degeneración crónica de los discos intervertebrales y pueden causar dolor." +
        "<li><b>Mínima protrusión discal posterior de base ancha:</b> Una protrusión discal es cuando una parte del disco intervertebral sobresale más allá de su límite normal. 'Base ancha' significa que la protuberancia ocupa una superficie amplia, y 'mínima' indica que el grado de sobresalimiento no es severo." +
        "<li><b>Contacto con la cara anterior del saco dural:</b> El saco dural es una membrana que rodea y protege la médula espinal. La protrusión discal está tocando esta membrana." +
        "<li><b>Espacios neuroforaminales:</b> Los forámenes son pequeños agujeros por donde pasan los nervios desde la médula espinal hacia las piernas. Esta protrusión está reduciendo el espacio por donde pasan los nervios, lo que puede causar dolor o síntomas neurológicos como hormigueo o debilidad." +
        "<li><b>Cono, epicono y raíces de la cola de caballo:</b> Estos son términos que describen partes de la médula espinal y los nervios cercanos." +
        "<li><b>Musculatura paravertebral:</b> Son los músculos cercanos a la columna." +
        "<li><b>Articulaciones facetarias:</b> Las articulaciones facetarias son las pequeñas articulaciones en la parte posterior de las vértebras." +
        "</ul>",
      causes: "<p>Una hernia discal o protrusión discal puede tener varias causas. Según el informe, los discos en los niveles L4-L5 y L5-S1 están afectados, lo cual es común en el envejecimiento o desgaste de la columna. Las principales causas y factores de riesgo son:</p>" +
        "<ul>" +
        "<li><b>Degeneración del disco:</b> Con la edad, los discos intervertebrales pierden agua y elasticidad, lo que los hace más propensos a protruir o herniarse." +
        "<li><b>Sobrecarga mecánica:</b> Levantar objetos pesados, posturas incorrectas o movimientos repetitivos pueden dañar los discos y favorecer la protrusión discal." +
        "<li><b>Lesiones o traumas:</b> Golpes fuertes o caídas pueden causar hernias discales." +
        "<li><b>Factores genéticos:</b> Algunas personas pueden tener predisposición a tener discos más débiles." +
        "<li><b>Sedentarismo y falta de ejercicio:</b> La debilidad muscular en la espalda y el abdomen puede aumentar el riesgo de hernias discales." +
        "<li><b>Obesidad:</b> El sobrepeso pone mayor presión sobre la columna, aumentando el desgaste de los discos." +
        "</ul>",
      treatment: "<p>El tratamiento de una hernia discal depende de la severidad de los síntomas y el impacto en la calidad de vida. Hay dos enfoques principales:</p>" +
        "<ul>" +
        "<li><b>Tratamientos conservadores (no quirúrgicos)</b>" +
        "<ul>" +
        "<li>Reposo relativo: Evitar actividades que agraven el dolor, pero mantener una movilidad moderada para evitar rigidez." +
        "<li>Fisioterapia: Ejercicios específicos para fortalecer los músculos de la espalda y el abdomen, mejorando la postura y reduciendo el dolor." +
        "<li>Analgésicos y antiinflamatorios: Medicamentos como ibuprofeno o paracetamol pueden ayudar a aliviar el dolor y la inflamación." +
        "<li>Terapias físicas: Técnicas como la electroterapia, ultrasonido o masoterapia pueden ayudar a reducir los síntomas." +
        "<li>Infiltraciones epidurales de esteroides: Inyecciones de corticoides en la columna para reducir la inflamación y el dolor en casos más severos." +
        "</ul>" +
        "<li><b>Tratamientos quirúrgicos</b>" +
        "<ul>" +
        "<li>Microdiscectomía: Una intervención mínimamente invasiva para remover la parte del disco que está comprimido los nervios." +
        "<li>Fusión vertebral: En casos de degeneración severa, se puede optar por fusionar dos vértebras para estabilizar la columna." +
        "</ul>",
      prevention: "<p>Para reducir el riesgo de problemas de columna en el futuro, se recomienda:</p>" +
        "<ul>" +
        "<li><b>Mantener una postura adecuada:</b> Sentarse y caminar con una buena alineación de la columna." +
        "<li><b>Ejercicio regular:</b> Fortalecer los músculos de la espalda y el abdomen mediante ejercicios como yoga, pilates o natación." +
        "<li><b>Evitar levantar objetos pesados:</b> O hacerlo de manera correcta, utilizando las piernas y no la espalda." +
        "<li><b>Control de peso:</b> Mantener un peso saludable para reducir la presión sobre la columna." +
        "<li><b>Evitar el sedentarismo:</b> Alternar entre estar de pie y sentado, y realizar pausas activas si se trabaja sentado por mucho tiempo." +
        "</ul>",
      complementary_tests: "<p>Para confirmar o complementar el diagnóstico de hernia discal, se pueden considerar las siguientes pruebas:</p>" +
        "<ul>" +
        "<li><b>Electromiografía (EMG):</b> Evalúa la función de los nervios y puede ayudar a detectar daños o compresión nerviosa." +
        "<li><b>Tomografía computarizada (TC):</b> Puede ofrecer una imagen más detallada de los huesos y las vértebras si hay sospecha de fracturas." +
        "<li><b>Mielografía:</b> Un estudio de imagen que usa contraste para visualizar mejor los nervios y la médula espinal." +
        "<li><b>Radiografía de columna:</b> Útil para evaluar el estado general de los huesos y la alineación vertebral." +
        "</ul>" +
        "<p>Estas pruebas pueden proporcionar más información sobre el estado de los nervios y confirmar si hay daños severos que justifiquen un tratamiento más invasivo</p>"
    }
  }

}
